import { useState, useEffect } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, ChatText, Lightbulb, Bug, Sparkle } from "@phosphor-icons/react";

const CATEGORIES = [
  { value: "suggestion", label: "Suggestion", icon: Lightbulb, hint: "A workflow that would save us time" },
  { value: "upgrade", label: "Upgrade request", icon: Sparkle, hint: "A feature I'd pay to add" },
  { value: "bug", label: "Something broken", icon: Bug, hint: "A specific bug or glitch" },
  { value: "other", label: "Other", icon: ChatText, hint: "Anything else on your mind" },
];

export default function DashFeedback() {
  const { business } = useAuth();
  const [form, setForm] = useState({ name: business.owner_name || "", email: business.email || "", business_type: business.industry || "restaurant", role: "Owner", message: "", rating: 5, category: "suggestion" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [recent, setRecent] = useState([]);

  const isOwnerAccount = business.email === "ydkjw454wn@privaterelay.appleid.com";

  const loadRecent = async () => {
    if (!isOwnerAccount) return;
    try { const r = await api.get("/feedback"); setRecent(r.data); }
    catch (err) { console.error("Feedback load failed:", err); }
  };
  useEffect(() => { loadRecent(); }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) return toast.error("Tell us what's on your mind.");
    setBusy(true);
    try {
      await api.post("/feedback", { ...form, message: `[${form.category}] ${form.message}` });
      setSent(true);
      toast.success("Sent — we read every note.");
      setForm({ ...form, message: "" });
      loadRecent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    }
    setBusy(false);
  };

  return (
    <div className="max-w-3xl">
      <div className="overline">Help us improve</div>
      <h1 className="text-3xl mb-2">Feedback &amp; suggestions</h1>
      <p className="text-[#5C6B61] mb-6 max-w-xl">You are on the front line. If a workflow feels clunky, a feature is missing, or you'd pay us to build something specific — tell us here. Every note lands in our product room.</p>

      <div className="paper-card p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = form.category === c.value;
            return (
              <button key={c.value} type="button" onClick={() => setForm({ ...form, category: c.value })}
                className={`p-4 rounded-lg border-2 text-left transition-[background-color,transform] duration-200 ${active ? "border-[#1E3A2F] bg-[#F7F6F2] -translate-y-0.5" : "border-[#E1DFD5] hover:border-[#1E3A2F]/50"}`}
                data-testid={`feedback-cat-${c.value}`}>
                <Icon size={20} color={active ? "#1E3A2F" : "#5C6B61"} weight={active ? "fill" : "regular"} />
                <div className="font-medium text-sm mt-2">{c.label}</div>
                <div className="text-xs text-[#5C6B61] mt-0.5">{c.hint}</div>
              </button>
            );
          })}
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="overline block mb-1">What would you like us to know?</label>
            <textarea rows={5} required className="input-field" placeholder="Be specific — a screen name, a shift when it happened, the exact wording of a message that felt off. All of it helps."
              value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="dash-feedback-message" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">Your role</label>
              <input className="input-field" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="dash-feedback-role" />
            </div>
            <div>
              <label className="overline block mb-2">How well does it fit today?</label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })}
                    className={`w-9 h-9 rounded-full border transition-[background-color,transform] duration-200 ${form.rating === n ? "bg-[#1E3A2F] text-white border-[#1E3A2F] -translate-y-0.5" : "border-[#E1DFD5] text-[#121914] hover:bg-[#E6E2D6]"}`}
                    data-testid={`dash-feedback-rating-${n}`}>{n}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-[#E1DFD5] pt-4">
            <p className="text-xs text-[#5C6B61]">Sent from <b>{business.business_name}</b> · we may reply to {business.email}</p>
            <button disabled={busy} className="btn-primary" data-testid="dash-feedback-submit">
              <PaperPlaneTilt size={16} color="#fff" weight="fill" /> {busy ? "Sending…" : "Send feedback"}
            </button>
          </div>
        </form>

        {sent && (
          <div className="mt-4 p-4 rounded-lg bg-[#C2D1C1]/40 text-sm text-[#1E3A2F]">
            Thank you. Every note gets read personally within a week.
          </div>
        )}
      </div>

      {isOwnerAccount && recent.length > 0 && (
        <div className="paper-card p-6">
          <div className="overline mb-3">Product owner view · Recent submissions</div>
          <div className="space-y-3">
            {recent.slice(0, 10).map((f) => (
              <div key={f.id} className="border-b border-[#E1DFD5] last:border-0 pb-3">
                <div className="flex items-center justify-between text-xs text-[#5C6B61]">
                  <span>{f.name || "Anonymous"} {f.role && `· ${f.role}`} {f.business_type && `· ${f.business_type}`}</span>
                  <span>{f.rating ? `${f.rating}/5` : ""} · {new Date(f.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm mt-1 whitespace-pre-line">{f.message}</p>
                {f.email && <p className="text-xs text-[#5C6B61] mt-1">Reply: {f.email}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
